import { Entity } from '@core/api/entity';
import { ProductStatusDTO } from '@core/dto/product.dto';
import { ArraySerializer, Field, Model, Name, PrimitiveSerializer, Serializer, Type } from '@junte/serialize-ts';

import { AssetClassValueObject, AssetClassValueObjectSerializer } from './asset-class-value-object';
import { Company } from './company.model';
import { FileObject } from './file-object.model';
import { ProductSectionStatuses } from './product-section-statuses.model';
import { NumberSerializer, ClientCompanySerializer } from './serializers';
import { SellingEligibilityDenialReason } from './selling-eligibility.model';
import { ValueObject, ValueObjectSerializer } from './value-object.model';

export type ProductStatus = ProductStatusDTO;

export interface ProductDdqRouteHintsDTO {
  productUid: string;
  issuerUid?: string | null;
}

export class ProductDdqRouteHintsSerializer implements Serializer<ProductDdqRouteHints> {
  public deserialize(dto: ProductDdqRouteHintsDTO | null | undefined): ProductDdqRouteHints | null {
    if (!dto) {
      return null;
    }
    return ProductDdqRouteHints.create({
      productUid: dto.productUid,
      issuerUid: dto.issuerUid ?? null,
    });
  }

  public serialize(model: ProductDdqRouteHints | null): ProductDdqRouteHintsDTO | null {
    if (!model) {
      return null;
    }
    return {
      productUid: model.productUid,
      ...(model.issuerUid != null && model.issuerUid !== '' ? { issuerUid: model.issuerUid } : {}),
    };
  }
}

@Model(new ProductDdqRouteHintsSerializer())
export class ProductDdqRouteHints {
  @Field()
  public productUid: string = null;

  @Field()
  public issuerUid: string | null = null;

  public static create(data: Partial<ProductDdqRouteHints>): ProductDdqRouteHints {
    return Object.assign(new ProductDdqRouteHints(), data);
  }
}

export interface ProductDdqSummaryDTO extends Record<string, any> {
  productUid: string;
  hasDdq: boolean;
  ddqStatus: string;
  accessSource: string;
  accessLevel: string;
  canView: boolean;
  viewDDQ?: boolean;
  canEdit: boolean;
  canBrowseOthers: boolean;
  showUpgradePrompt: boolean;
  primaryDdqUid: string | null;
  ddqUids: string[];
  latestUpdatedAt: string | null;
  complianceOk: boolean;
  denialReason: string | null;
  issuerUid?: string | null;
  routeHints: ProductDdqRouteHintsDTO;
}

export class ProductDdqSummarySerializer implements Serializer<ProductDdqSummary> {
  public deserialize(dto: ProductDdqSummaryDTO | null | undefined): ProductDdqSummary | null {
    if (!dto) {
      return null;
    }

    return ProductDdqSummary.create({
      productUid: dto.productUid,
      hasDdq: dto.hasDdq,
      ddqStatus: dto.ddqStatus,
      accessSource: dto.accessSource,
      accessLevel: dto.accessLevel,
      canView: dto.canView,
      viewDDQ: dto.viewDDQ ?? dto.canView,
      canEdit: dto.canEdit,
      canBrowseOthers: dto.canBrowseOthers,
      showUpgradePrompt: dto.showUpgradePrompt,
      primaryDdqUid: dto.primaryDdqUid ?? null,
      ddqUids: dto.ddqUids ?? [],
      latestUpdatedAt: dto.latestUpdatedAt ?? null,
      complianceOk: dto.complianceOk,
      denialReason: dto.denialReason ?? null,
      issuerUid: dto.issuerUid ?? null,
      routeHints: dto.routeHints
        ? ProductDdqRouteHints.create({
            productUid: dto.routeHints.productUid,
            issuerUid: dto.routeHints.issuerUid ?? null,
          })
        : null,
    });
  }

  public serialize(model: ProductDdqSummary | null): ProductDdqSummaryDTO | null {
    if (!model) {
      return null;
    }
    return {
      productUid: model.productUid,
      hasDdq: model.hasDdq,
      ddqStatus: model.ddqStatus,
      accessSource: model.accessSource,
      accessLevel: model.accessLevel,
      canView: model.canView,
      viewDDQ: model.viewDDQ,
      canEdit: model.canEdit,
      canBrowseOthers: model.canBrowseOthers,
      showUpgradePrompt: model.showUpgradePrompt,
      primaryDdqUid: model.primaryDdqUid,
      ddqUids: model.ddqUids,
      latestUpdatedAt: model.latestUpdatedAt,
      complianceOk: model.complianceOk,
      denialReason: model.denialReason,
      issuerUid: model.issuerUid ?? null,
      routeHints: model.routeHints
        ? {
            productUid: model.routeHints.productUid,
            ...(model.routeHints.issuerUid != null && model.routeHints.issuerUid !== ''
              ? { issuerUid: model.routeHints.issuerUid }
              : {}),
          }
        : null,
    };
  }
}

@Model(new ProductDdqSummarySerializer())
export class ProductDdqSummary {
  public productUid: string = null;
  public hasDdq: boolean = false;
  public ddqStatus: string = 'unknown';
  public accessSource: string = 'none';
  public accessLevel: string = 'read_only';
  public canView: boolean = false;
  public viewDDQ: boolean = false;
  public canEdit: boolean = false;
  public canBrowseOthers: boolean = false;
  public showUpgradePrompt: boolean = false;
  public primaryDdqUid: string | null = null;
  public ddqUids: string[] = [];
  public latestUpdatedAt: string | null = null;
  public complianceOk: boolean = true;
  public denialReason: string | null = null;
  public issuerUid: string | null = null;
  public routeHints: ProductDdqRouteHints | null = null;

  public static create(data: Partial<ProductDdqSummary>): ProductDdqSummary {
    return Object.assign(new ProductDdqSummary(), data);
  }
}

/** Issuer UID for fund-ddq-runs / emittent-scoped DDQ calls (API may set on routeHints or summary). */
export function getDdqIssuerUidFromProduct(
  product: { ddqSummary?: ProductDdqSummary | null } | null | undefined,
): string | null {
  const raw = product?.ddqSummary?.routeHints?.issuerUid ?? product?.ddqSummary?.issuerUid ?? null;
  if (raw == null) {
    return null;
  }
  const s = String(raw).trim();
  return s === '' ? null : s;
}

/** Product UID for DDQ-scoped APIs (routeHints vs summary productUid), aligned with product-details-card navigation. */
export function getDdqProductUidFromProduct(
  product: { ddqSummary?: ProductDdqSummary | null } | null | undefined,
): string | null {
  const raw = product?.ddqSummary?.routeHints?.productUid ?? product?.ddqSummary?.productUid ?? null;
  if (raw == null) {
    return null;
  }
  const s = String(raw).trim();
  return s === '' ? null : s;
}

@Model()
export class Product extends Entity {
  @Field()
  uid: string;

  @Field()
  @Type(new AssetClassValueObjectSerializer())
  public assetClass: AssetClassValueObject;

  @Field()
  @Type(new AssetClassValueObjectSerializer())
  public assetSubclass: AssetClassValueObject;

  @Field()
  public keepPrivate: boolean;

  @Field()
  @Type(new ClientCompanySerializer())
  public clientCompany: string | number | null;

  @Field()
  public emittentCompany: Company;

  @Field()
  public esgCompliance: null | 0 | 6 | 8 | 9;

  @Field()
  public image: FileObject;
  @Field()
  public bannerImage: FileObject;

  @Field()
  @Type(new ArraySerializer(new ValueObjectSerializer()))
  public industries: ValueObject[];

  @Field()
  @Type(new ArraySerializer(new ValueObjectSerializer()))
  public investmentCategories: ValueObject[];

  @Field()
  @Type(new NumberSerializer())
  public maxInterestPerDividendOnIssue: number;

  @Field()
  @Type(new NumberSerializer())
  public maxInvestment: number;

  @Field()
  @Type(new NumberSerializer())
  public raisedFunds: number;
  @Field()
  @Type(new NumberSerializer())
  public availableFunds: number;

  @Field()
  @Type(new NumberSerializer())
  public maxIssuePricePerUnit: number;

  @Field()
  @Type(new NumberSerializer())
  public maxIssueValue: number;

  @Field()
  @Type(new NumberSerializer())
  public maxRate: number;

  @Field()
  @Type(new NumberSerializer())
  public minInterestPerDividendOnIssue: number;

  @Field()
  @Type(new NumberSerializer())
  public minInvestment: number;

  @Field()
  @Type(new NumberSerializer())
  public minIssuePricePerUnit: number;

  @Field()
  @Type(new NumberSerializer())
  public minIssueValue: number;

  @Field()
  @Type(new NumberSerializer())
  public minRate: number;

  @Field()
  public duration: '10-years' | '10-years-plus-3x1' | '10-years-plus-2x1';

  @Field()
  public customDuration: string;

  @Field()
  public maximumLoss: 'no_loss' | 'minor_loss' | 'total_loss' | 'greater_loss';

  @Field()
  @Name('productName')
  public productName: string;

  @Field()
  @Type(new NumberSerializer())
  public nominalNumberOfUnits: number;

  @Field()
  @Type(new NumberSerializer())
  public nominalUnitValue: number;

  @Field()
  @Type(new NumberSerializer())
  public nominalValueDuringIssue: number;

  @Field()
  @Type(new NumberSerializer())
  public numberOfUnitsDuringIssue: number;

  @Field()
  @Type(new NumberSerializer())
  public numberOfUnitsIssued: number;

  @Field()
  @Type(new ArraySerializer(new ValueObjectSerializer()))
  public permittedInvestorClasses: ValueObject[];

  @Field()
  public processDescription: string;

  @Field()
  public productNumber: string;

  @Field()
  @Type(new PrimitiveSerializer())
  public defaultLanguage: number | string;

  @Field()
  @Type(new ArraySerializer(new PrimitiveSerializer()))
  public availableTranslations: string[];

  @Field()
  @Type(new ValueObjectSerializer())
  public rating: ValueObject;

  @Field()
  @Type(new ValueObjectSerializer())
  public country: ValueObject;

  @Field()
  public shortProfileDescription: string;

  @Field()
  public sectionStatuses: ProductSectionStatuses;

  @Field()
  @Type(new PrimitiveSerializer())
  public status: ProductStatus;

  @Field()
  public termUntil: string;
  @Field()
  public releaseDate: string;
  @Field()
  @Type(new NumberSerializer())
  public totalNominalValue: number;

  @Field()
  @Type(new NumberSerializer())
  public totalNominalValueAfterIssue: number;

  @Field()
  public useFixedInterestsModel: boolean;

  @Field()
  public useFixedRateModel: boolean;

  @Field()
  public productMarketPlacement: 'public' | 'private';

  @Field()
  public initialInvestmentVolume: number;

  @Field()
  public currentInvestmentVolume: number;

  @Field()
  public minimumIncrement: number;

  @Field()
  public paymentType: 'initial' | 'capital_call';

  @Field()
  public riskClassification: 'low' | 'medium' | 'high' | 'very_high';

  @Field()
  public indicativeAnnualReturn: string;

  @Field()
  public fundType: 'open' | 'closed';

  @Field()
  public fundStructure: string;

  @Field()
  public lockInPeriod: number;

  @Field()
  public liquidityRestrictions: string;

  @Field()
  public fundInvestmentMethod: string;

  @Field()
  public actualReturnDate: string;
  @Field()
  public targetReturn: string;

  @Field()
  public issuingId: string;

  @Field()
  public canInvest?: boolean;

  @Field()
  @Type(new PrimitiveSerializer())
  public denialReason?: SellingEligibilityDenialReason | null;

  @Type(new ProductDdqSummarySerializer())
  public ddqSummary?: ProductDdqSummary | null;

  @Field()
  public isFeaturedEligible: boolean;

  public static create(data: Partial<Product>): Product {
    return Object.assign(new Product(), data);
  }

  /**
   * To preserve compatibility with other percentage fields it returns
   * fractional values multiplied by 100. E.g. `0.2137` is `21.37`.
   */
  public getShareOfIssuedInTotal(): number {
    const divisorValue = this.nominalNumberOfUnits + this.numberOfUnitsDuringIssue;
    return divisorValue ? (this.numberOfUnitsIssued / divisorValue) * 100 : NaN;
  }
}
